// Našeptávač (issue #31, refaktor #74, vlastní režim #97;
// screens/17-vyhledavani.md §„Desktop — našeptávač", mockup design/SiteHeader.dc.html).
//
// Modul má DVA REŽIMY nad jednou kotvou `#header .search` a jednou sadou stylů:
//
//   • **nativní** (fallback, #31) — obsah plní DEFAULT JS Shoptetu do
//     `div.searchWhisperer` a plní ho ZNOVU při každém stisku. Proto se
//     rozvržení dělá CSS gridem nad nativními bloky, ne přeskládáním DOM,
//     a naše přídavky se dosazují idempotentně z MutationObserveru.
//     Nativní uzly se otagují našimi `bs-wh-*` třídami (mapa `SKIN`).
//   • **vlastní** (#97) — obsah bere `hledani.jipos.cz` (`/autocomplete`,
//     `/popular`) a `whispererRender.js` z něj postaví TUTÉŽ `bs-wh-*`
//     strukturu do vlastního `div.bs-wh--own`. Nativní panel se schová CSS
//     (`body.bs-ac-live`), ale zůstane naplněný — je to fallback na jeden
//     stisk klávesy.
//
// **Dva moduly na téhle kotvě být nesmějí** (koncepce §Důsledky pro zbytek
// epicu) — skákaly by si do renderu. `whispererRender.js` je čistý renderer
// bez kotvy, ne druhý ovladač.
//
// PŘEPÍNÁNÍ BEZ FOUC (koncepce §Přepínání nativ ↔ vlastní):
//   | náš request běží, panel prázdný | `bs-ac-pending` | náš skeleton     |
//   | odpověď s obsahem               | `bs-ac-live`    | náš panel        |
//   | prázdno / chyba / > 900 ms      | žádná           | nativní whisperer|
// Při přepisu dotazu se obsah **jen vymění** (`replaceChildren` v jednom
// commitu) — panel nikdy neprobliká prázdný, jak to dělá nativ.
//
// Klávesnice je povinná: role="listbox"/"option", aria-activedescendant,
// šipky, Enter otevře zvýrazněnou položku, Esc zavře. Enter musí PŘEBÍT
// nativní handler → listener v capture fázi na `document` (trik z night-notes #7;
// `stopImmediatePropagation` na inputu nestačí, Shoptet se registruje dřív).
//
// Mobil: panel je obsahem fullscreen sheetu (mobileSearch.js). Zero-state tam
// nejedeme — sheet má vlastní prázdný stav (chipy + kategorie), dva by se
// překrývaly. Sladí se, až budou reálné fráze (#88).

import { icon, iconHtml } from './icon.js';
import { CAT_ICONS, readMenu } from './menuModel.js';
import { POPULAR, suggestQueries, searchUrl } from './searchSuggest.js';
import { getConfig } from './searchConfig.js';
import { autocomplete, popular } from './searchApi.js';
import { renderWhisperer, renderSkeleton } from './whispererRender.js';
import {
  trackSearch,
  trackNodeClick,
  wireClicks,
  SERVICE_AUTOCOMPLETE,
  LIST_AUTOCOMPLETE,
} from './searchAnalytics.js';
import { addToCart } from '../shoptet-hooks.js';
import { page } from './pages.js';
import L from '../locale/cs/searchWhisperer.js';

const LABEL_SUGGESTS = L.suggests;
const LABEL_HINT = L.hint;
const ITEM_ID = 'bs-wh-item-';

// Debounce a práh: nativ má 500 ms / 3 znaky, takže jsme vždy dřív
// (koncepce §Přepínání). Limity sekcí drží panel na 5 řádcích dle mockupu.
const DEBOUNCE_MS = 140;
const MIN_CHARS = 2;
const AC_PRODUCTS = 5;
const AC_QUERIES = 5;

// Zero-state: počty jsou vyladěné tak, aby levý sloupec (fráze + kategorie)
// vyšel výškově zhruba na pravý (produkty + patka) — jinak panel zbytečně
// přeroste a pod patkou zůstane prázdné bílé pole.
const ZERO_PRODUCTS = 5;
const ZERO_QUERIES = 6;
const ZERO_CATS = 5;

// Slevoakce má v hlavičce vlastní chip a v zero-state není kategorie jako
// každá jiná — stejný důvod jako `BAR_SKIP` v megaMenu.js.
const ZERO_SKIP = [page('sale')];

// Rotující placeholder. Fráze jsou zatím kurátorské (`searchSuggest.POPULAR`),
// reálné dotazy z vlastního logu je nahradí v #88.
const PLACEHOLDER_MS = 3200;
const PLACEHOLDER_PREFIX = L.placeholderPrefix;
const PLACEHOLDER_SUFFIX = L.placeholderSuffix;

const MQ_MOBILE = '(max-width: 768px)';

// Nativní blok → naše třída, na které visí SCSS (#74). Idempotentní: běží po
// každém překreslení panelu default JS.
//
// TODO(#74): pokrývá variantu s feature flagem `extended_search_whisperer`.
// Bez flagu Shoptet renderuje `div.search-whisperer` DOVNITŘ fieldsetu a s
// jinými jmény tříd (CLAUDE.md §Gotchas) — ta varianta nebyla nastylovaná ani
// před refaktorem; doplnit, až bude fixture. Dev instance flag zapnutý má.
const SKIN = [
  ['.searchWhisperer__additionalResults', 'bs-wh__side'],
  ['.searchWhisperer__additionalResults__heading', 'bs-wh-eyebrow'],
  ['.searchWhisperer__additionalResults__list', 'bs-wh-list'],
  ['.searchWhisperer__additionalResults__link', 'bs-wh-link'],
  ['.searchWhisperer__products', 'bs-wh__main'],
  ['.searchWhisperer__products__heading', 'bs-wh-eyebrow bs-wh-eyebrow--main'],
  ['.searchWhisperer__products__list', 'bs-wh-plist'],
  ['.searchWhisperer__product', 'bs-wh-prod'],
  ['.searchWhisperer__product__image', 'bs-wh-prod__image'],
  ['.searchWhisperer__product__infoWrapper', 'bs-wh-prod__info'],
  ['.searchWhisperer__product__name', 'bs-wh-prod__name'],
  ['.searchWhisperer__product__availability', 'bs-wh-prod__avail'],
  ['.searchWhisperer__product__price', 'bs-wh-prod__price'],
  ['.searchWhisperer__buttonWrapper', 'bs-wh__foot'],
  ['.searchWhisperer__button', 'bs-wh__cta'],
];

let host = null;
let input = null;
let panel = null; // nativní `div.searchWhisperer` (plní ho default JS)
let own = null; // náš `div.bs-wh--own` (plní ho whispererRender)
let items = [];
let active = -1;
let apiOn = false;

// `wireClicks` si drží referenci na tenhle objekt a čte ho až v okamžiku
// prokliku → dotaz stačí přepsat, listener se neváže znovu. `service` a
// `listId` označují v GA4 našeptávač (LB `service: AutoComplete`).
const clickCtx = { query: '', service: SERVICE_AUTOCOMPLETE, listId: LIST_AUTOCOMPLETE };

let debounce = null;
let seq = 0; // pořadové číslo requestu — starší odpověď se zahodí
let trackedQuery = null;

/**
 * Panel, který uživatel právě vidí — jediný zdroj pravdy pro klávesnici.
 * Rozhodují tytéž třídy na `body`, kterými se panely přepínají v CSS, takže
 * se stav JS a stav renderu nemůžou rozejít. `null` = zavřeno (nebo skeleton,
 * ve kterém není co vybírat).
 */
function openPanel() {
  const flags = document.body.classList;
  if (flags.contains('bs-ac-pending')) return null;
  if (flags.contains('bs-ac-live')) return own && own.children.length ? own : null;
  return panel && panel.children.length ? panel : null;
}

function isOpen() {
  return !!openPanel();
}

function isMobile() {
  try {
    return window.matchMedia(MQ_MOBILE).matches;
  } catch (e) {
    return false;
  }
}

// Otevřený fullscreen sheet mobilního hledání (třídu přepíná mobileSearch.js).
// Čte se z DOM, ne importem — modul se nesmí vázat na sheet, jede i bez něj.
function inSheet() {
  return document.body.classList.contains('bs-search-open');
}

function skinNative() {
  SKIN.forEach(function (pair) {
    panel.querySelectorAll(pair[0]).forEach(function (node) {
      node.classList.add.apply(node.classList, pair[1].split(' '));
    });
  });
}

// --------------------------------------------------- přídavky nativního panelu

function buildSuggests(query) {
  const list = suggestQueries(query, 5);
  if (!list.length) return null;

  const box = document.createElement('div');
  box.className = 'bs-wh-sug';
  const head = document.createElement('div');
  head.className = 'bs-wh-eyebrow';
  head.textContent = LABEL_SUGGESTS;
  box.appendChild(head);

  list.forEach(function (s) {
    const link = document.createElement('a');
    link.className = 'bs-wh-sug__item';
    link.href = searchUrl(s.query);
    link.appendChild(icon('search'));
    const hit = document.createElement('span');
    hit.className = 'bs-wh-sug__hit';
    hit.textContent = s.hit;
    link.appendChild(hit);
    if (s.rest) {
      const rest = document.createElement('span');
      rest.className = 'bs-wh-sug__rest';
      rest.textContent = s.rest;
      link.appendChild(rest);
    }
    box.appendChild(link);
  });

  return box;
}

// Nativní panel má vlastní patku s tlačítkem „Zobrazit všechny výsledky"
// (submit formu). Popisek zůstává nativní — celkový počet výsledků Shoptet
// nikde nevystavuje (viz searchPage.js), takže „Zobrazit všech N" by lhalo.
// Vlastní panel počet zná z indexu a píše ho (whispererRender.resultsCta).
function enhanceFooter() {
  const foot = panel.querySelector('.bs-wh__foot');
  if (!foot || foot.querySelector('.bs-wh__hint')) return;
  const btn = foot.querySelector('.bs-wh__cta');
  if (btn && !btn.querySelector('.bs-icon')) btn.insertAdjacentHTML('beforeend', iconHtml('arrow-right'));
  const hint = document.createElement('span');
  hint.className = 'bs-wh__hint';
  hint.textContent = LABEL_HINT;
  hint.setAttribute('aria-hidden', 'true');
  foot.appendChild(hint);
}

// Nativní markup je pro kategorie, značky i články TOTOŽNÝ — liší se jen
// třída nadpisu nad seznamem (`--category` / `--brand` / `--article`).
// Podle něj seznam otagujeme (chipy vs. řádky) a řádkům dosadíme ikonu.
const LIST_KINDS = { category: 'cat', brand: 'brand', article: 'article' };

function tagLists() {
  panel.querySelectorAll('.bs-wh-list').forEach(function (list) {
    if (list.dataset.bsKind) return;
    const head = list.previousElementSibling;
    let kind = '';
    if (head) {
      for (const key in LIST_KINDS) {
        if (head.classList.contains('searchWhisperer__additionalResults__heading--' + key)) kind = LIST_KINDS[key];
      }
    }
    list.dataset.bsKind = kind || 'other';
    if (kind) list.classList.add('bs-wh-list--' + kind);
    if (kind === 'brand') return; // chipy jedou bez ikony

    list.querySelectorAll('.bs-wh-link').forEach(function (link) {
      if (link.querySelector('.bs-wh-ico')) return;
      const label = (link.textContent || '').trim();
      link.textContent = '';
      const box = document.createElement('span');
      box.className = 'bs-wh-ico';
      box.appendChild(icon(kind === 'article' ? 'file' : catIcon(link.getAttribute('href'))));
      link.appendChild(box);
      const span = document.createElement('span');
      span.className = 'bs-wh-label';
      span.textContent = label;
      link.appendChild(span);
    });
  });
}

function catIcon(href) {
  const path = (href || '').replace(/^https?:\/\/[^/]+/, '');
  return CAT_ICONS[path] || 'grid';
}

// --------------------------------------------------------------- klávesnice

function collectItems() {
  const root = openPanel();
  if (!root) {
    items = [];
    active = -1;
    if (input) {
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }
    return;
  }

  items = Array.prototype.slice.call(
    root.querySelectorAll('.bs-wh-sug__item, .bs-wh-prod, .bs-wh-link, .bs-wh__cta'),
  );
  items.forEach(function (node, i) {
    node.id = ITEM_ID + i;
    node.setAttribute('role', 'option');
    node.setAttribute('aria-selected', 'false');
  });
  active = -1;
  if (input) {
    input.setAttribute('aria-expanded', 'true');
    input.setAttribute('aria-controls', root.id);
    input.removeAttribute('aria-activedescendant');
  }
}

function setActive(index) {
  if (!items.length) return;
  if (active >= 0 && items[active]) {
    items[active].classList.remove('is-active');
    items[active].setAttribute('aria-selected', 'false');
  }
  active = (index + items.length) % items.length;
  const node = items[active];
  node.classList.add('is-active');
  node.setAttribute('aria-selected', 'true');
  if (input) input.setAttribute('aria-activedescendant', node.id);
  if (node.scrollIntoView) node.scrollIntoView({ block: 'nearest' });
}

function clearActive() {
  if (active >= 0 && items[active]) {
    items[active].classList.remove('is-active');
    items[active].setAttribute('aria-selected', 'false');
  }
  active = -1;
  if (input) input.removeAttribute('aria-activedescendant');
}

/** Kam vede položka — `<a>` má href, náš produktový řádek `data-bs-href`. */
function itemHref(node) {
  if (node.tagName === 'A' && node.href) return node.href;
  return node.dataset && node.dataset.bsHref ? node.dataset.bsHref : '';
}

// Chrome u `type=search` na Escape sám vymaže hodnotu a vystřelí `input`
// event — bez téhle značky by onInput hned po zavření znovu otevřel
// zero-state a Esc by panel nikdy nezavřel (nález U1, 2026-08-12).
let escAt = 0;

// POZOR: capture fáze na `document`, ne listener na inputu — nativní handler
// Shoptetu se registruje dřív a Enter by nám utekl do submitu formu.
function onKeyDown(e) {
  if (!input || e.target !== input) return;

  if (e.key === 'Escape') {
    escAt = Date.now();
    if (apiOn) closeOwn();
    clearActive();
    return;
  }
  if (!isOpen()) return;

  if (e.key === 'ArrowDown') {
    e.preventDefault();
    setActive(active + 1);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    setActive(active - 1);
  } else if (e.key === 'Enter') {
    if (active < 0 || !items[active]) return; // Enter bez výběru = nativní odeslání
    e.preventDefault();
    e.stopPropagation(); // capture fáze → nativní listener se vůbec nespustí
    const node = items[active];
    const href = itemHref(node);
    // Navigace přes `location.href` žádný `click` nevydá (fokus drží input),
    // takže `wireClicks` by proklik minul — hlásí se tady, PŘED navigací (jen
    // náš panel; nativní se neměří ani myší). Větev `node.click()` ho naopak
    // vydá, a vede-li uzel někam, zapíše si `wireClicks` proklik sám — hlásit
    // ho i tady by bylo dvakrát.
    if (href) {
      if (openPanel() === own) trackNodeClick(own, node, clickCtx);
      window.location.href = href;
    } else node.click();
  }
}

// ------------------------------------------------------- render nativní cesty

function enhance() {
  if (!panel) return;
  if (!panel.children.length) {
    // default JS panel vyprázdnil (krátký dotaz / žádná shoda)
    panel.classList.remove('bs-wh--sided');
    if (!openPanel()) collectItems();
    return;
  }

  panel.classList.add('bs-wh');
  panel.setAttribute('role', 'listbox');

  skinNative(); // nativní jména → naše `bs-wh-*` (SCSS visí jen na nich)

  const side = panel.querySelector('.bs-wh__side');
  if (!panel.querySelector('.bs-wh-sug')) {
    const sug = buildSuggests(input ? input.value : '');
    if (sug) {
      if (side) side.insertAdjacentElement('afterbegin', sug);
      else panel.insertAdjacentElement('afterbegin', sug);
    }
  }
  panel.classList.toggle('bs-wh--sided', !!(side || panel.querySelector('.bs-wh-sug')));

  tagLists();
  enhanceFooter();
  // Náš panel má přednost — dokud drží obsah, nativní zůstává jen připravený
  // fallback a klávesnice patří jemu.
  if (openPanel() !== own) collectItems();
}

// -------------------------------------------------------- render vlastní cesty

/** Odpověď `/autocomplete` → model pro `whispererRender`. `null` = degraduj. */
function modelFromAutocomplete(data, query) {
  if (!data) return null;

  // Našeptané dotazy index zatím nemá — obsahová kolekce `queries` pro tenanta
  // `jipos-cz` nakonfigurovaná není a vrací `[]` (ověřeno 2026-08-03). Do #88
  // je proto plní kurátorský seznam ze `searchSuggest.js`, stejně jako nativní
  // cestu. Až budou reálné fráze, stačí, že je API začne vracet.
  const fromApi = (data.queries || []).map(function (item) {
    return { query: item.name || item.query || String(item) };
  });

  return {
    mode: 'query',
    query: query,
    suggests: fromApi.length ? fromApi.slice(0, AC_QUERIES) : suggestQueries(query, AC_QUERIES),
    groups: [
      { kind: 'cat', items: data.categories },
      { kind: 'brand', items: data.brands },
      { kind: 'article', items: data.articles },
      { kind: 'page', items: data.pages },
    ],
    products: data.products || [],
    total: typeof data.total === 'number' ? data.total : 0,
    url: data.url || searchUrl(query),
    urlFor: searchUrl,
  };
}

/** Zero-state: populární fráze + kategorie z menu + bestsellery z `/popular`. */
function zeroModel(data) {
  const cats = readMenu()
    .filter(function (root) {
      return ZERO_SKIP.indexOf(root.path) === -1;
    })
    .slice(0, ZERO_CATS)
    .map(function (root) {
      return { name: root.label, url: root.href };
    });

  return {
    mode: 'zero',
    query: '',
    suggests: POPULAR.slice(0, ZERO_QUERIES).map(function (q) {
      return { query: q };
    }),
    groups: [{ kind: 'cat', items: cats }],
    products: ((data && data.items) || []).slice(0, ZERO_PRODUCTS),
    urlFor: searchUrl,
  };
}

function commit(model) {
  const content = renderWhisperer(model);
  if (!content.childNodes.length) {
    closeOwn();
    return false;
  }

  // JEDEN commit = žádné blikání: panel se vymění, nikdy se nevyprázdní
  // a nenaskočí znovu (akceptační bod 4 issue #97).
  own.replaceChildren(content);
  delete own.dataset.bsSkel;
  own.classList.toggle('bs-wh--sided', !!own.querySelector('.bs-wh__side'));
  document.body.classList.remove('bs-ac-pending');
  document.body.classList.add('bs-ac-live');
  clickCtx.query = model.query || '';
  collectItems();
  return true;
}

function showSkeleton() {
  if (own.children.length) return; // máme co ukázat → stale-while-revalidate
  own.replaceChildren(renderSkeleton());
  own.dataset.bsSkel = '1';
  own.classList.add('bs-wh--sided');
  document.body.classList.add('bs-ac-pending');
  document.body.classList.remove('bs-ac-live');
  items = [];
  active = -1;
}

/** Zavře vlastní panel a vrátí scénu nativnímu whispereru (fallback Ú1). */
function closeOwn() {
  seq += 1; // odpověď běžícího requestu se už nepoužije
  if (debounce) clearTimeout(debounce);
  debounce = null;
  if (!own) return;
  own.replaceChildren();
  delete own.dataset.bsSkel;
  own.classList.remove('bs-wh--sided');
  document.body.classList.remove('bs-ac-pending', 'bs-ac-live');
  collectItems();
}

async function runQuery(query) {
  const my = ++seq;
  showSkeleton();

  const data = await autocomplete(query, { products: AC_PRODUCTS, queries: AC_QUERIES });
  if (my !== seq) return; // mezitím doběhl novější dotaz
  if (input.value.trim() !== query) return;

  const model = modelFromAutocomplete(data, query);
  // Prázdná odpověď je pro nás totéž co chyba — nativ může mít víc.
  if (!model || (!model.products.length && !model.groups.some(hasItems))) {
    closeOwn();
    return;
  }
  if (!commit(model)) return;

  if (trackedQuery !== query) {
    trackedQuery = query;
    trackSearch({
      query: query,
      results: model.total,
      took: data.took_ms,
      engine: 'typesense',
      suggest: true,
    });
  }
}

function hasItems(group) {
  return !!(group.items && group.items.length);
}

async function runZero() {
  if (isMobile()) return; // sheet má vlastní prázdný stav (mobileSearch.js)
  const my = ++seq;
  // Sezónní skóre jako řada homepage (2026-09-21) — holá tržba 90 dní nesla
  // v září letní zboží (obrubník, kalová čerpadla) a zimní trhák ne.
  const data = await popular(ZERO_PRODUCTS, 'seasonal');
  if (my !== seq) return;
  if (input.value.trim()) return; // uživatel mezitím začal psát
  commit(zeroModel(data)); // bez `/popular` zůstanou fráze a kategorie
}

function onInput() {
  if (debounce) clearTimeout(debounce);
  debounce = null;
  const query = input.value.trim();

  if (query.length < MIN_CHARS) {
    closeOwn();
    // smazaný dotaz = zpátky na prázdný stav; výmaz způsobený Escape
    // (Chrome u type=search) ale panel otevírat nesmí
    if (!query && Date.now() - escAt > 200) runZero();
    return;
  }
  debounce = setTimeout(function () {
    runQuery(query);
  }, DEBOUNCE_MS);
}

// Košík z řádku panelu. Delegace, ne listener na řádku — obsah se vyměňuje
// při každém dotazu. Nákup dál obsluhuje výhradně Shoptet
// (`shoptet.cartShared.addToCart`), jen se volá kódem místo formuláře.
function onOwnClick(e) {
  const btn = e.target && e.target.closest && e.target.closest('[data-bs-cart]');
  if (!btn || !own.contains(btn)) return;
  e.preventDefault();
  if (!addToCart({ productCode: btn.dataset.bsCart, amount: 1 })) {
    const row = btn.closest('[data-bs-href]');
    if (row && row.dataset.bsHref) window.location.href = row.dataset.bsHref;
  }
}

// ------------------------------------------------------- rotující placeholder

let phTimer = null;
let phIndex = -1;
let phOriginal = '';

function tickPlaceholder() {
  // za fokusem, s rozepsaným dotazem a na skryté kartě se nic nemění
  if (!input || document.hidden || input.value || document.activeElement === input) return;
  phIndex = (phIndex + 1) % POPULAR.length;
  input.placeholder = PLACEHOLDER_PREFIX + POPULAR[phIndex] + PLACEHOLDER_SUFFIX;
}

function startPlaceholder() {
  if (!input || phTimer || POPULAR.length < 2) return;
  try {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  } catch (e) {
    /* starý prohlížeč — rotace se pustí */
  }
  phTimer = setInterval(tickPlaceholder, PLACEHOLDER_MS);
}

function stopPlaceholder() {
  if (phTimer) clearInterval(phTimer);
  phTimer = null;
  phIndex = -1;
  if (input) input.placeholder = phOriginal;
}

// --------------------------------------------------------------------- init

// POZOR: `div.searchWhisperer` v serverovém HTML NENÍ — default JS ho zakládá
// jako sourozence formu až při prvním psaní (ověřeno na dev instanci
// 2026-07-28; ve fixtures je zachycený, protože ty se stahují až po doběhnutí
// JS). Proto se věšíme na `div.search` a panel si počkáme.
export function initSearchWhisperer() {
  host = document.querySelector('#header .search');
  if (!host || host.dataset.bsWhisperer) return;
  host.dataset.bsWhisperer = '1';
  input = host.querySelector('.query-input') || document.querySelector('#header .query-input');

  if (input) {
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-autocomplete', 'list');
    phOriginal = input.placeholder || '';
    // capture na dokumentu: Enter s vybranou položkou musí přebít nativ
    document.addEventListener('keydown', onKeyDown, true);
    startPlaceholder();
  }

  // Vlastní režim se pustí jen tam, kde smí (koncepce §Fallback Ú0: LB na
  // stránce, ceník, který index nenese (jen 1 a VO 13/16/19), breaker, neznámý projectId, `file://`).
  apiOn = !!(input && getConfig());
  if (apiOn) initOwnMode();
  else if (input) watchForRecovery();

  function sync() {
    if (!panel || !panel.isConnected) {
      panel = host.querySelector('.searchWhisperer, .search-whisperer');
      if (!panel) return;
      if (!panel.id) panel.id = 'bs-whisperer';
    }
    enhance();
  }

  // panel vzniká i překresluje se za běhu (každý stisk) → dorovnáváme po každé
  // změně uvnitř `.search`; dávka mutací se sbalí do jednoho běhu (rAF).
  // Změny UVNITŘ našeho panelu se přeskakují — jinak by si vlastní render
  // pokaždé zavolal dorovnání nativního.
  let queued = false;
  new MutationObserver(function (records) {
    if (own && records.every(ownMutation)) return;
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      sync();
    });
  }).observe(host, { childList: true, subtree: true });

  sync();
}

function ownMutation(record) {
  return own.contains(record.target);
}

/**
 * Stránka naběhla s vrstvou vypnutou — typicky shozený breaker (5xx z API,
 * TTL 5 min, searchConfig.js). Po uplynutí TTL se `getConfig()` přepočítá sám,
 * ale našeptávač by bez tohohle zůstal nativní až do další stránky (Codex review
 * PR #377). Při každé interakci s polem se proto zeptá znovu (u LB nebo
 * nepodporovaného ceníku je to levné čtení cache guardů) a vlastní režim
 * zapne, jakmile smí. Listenery přidané během dispatch téhož eventu by ho
 * minuly — první událost se proto dohraje ručně.
 */
function watchForRecovery() {
  function upgrade(e) {
    if (apiOn || !getConfig()) return;
    apiOn = true;
    input.removeEventListener('focus', upgrade);
    input.removeEventListener('input', upgrade);
    initOwnMode();
    if (e.type === 'input') {
      onInput();
    } else {
      stopPlaceholder();
      if (input.value.trim().length < MIN_CHARS) runZero();
    }
  }
  input.addEventListener('focus', upgrade);
  input.addEventListener('input', upgrade);
}

function initOwnMode() {
  own = document.createElement('div');
  own.id = 'bs-wh-own';
  own.className = 'bs-wh bs-wh--own';
  own.setAttribute('role', 'listbox');

  const form = host.querySelector('.search-form') || host.querySelector('form');
  if (form && form.nextSibling) host.insertBefore(own, form.nextSibling);
  else host.appendChild(own);

  own.addEventListener('click', onOwnClick);
  wireClicks(own, clickCtx);

  input.addEventListener('input', onInput);
  input.addEventListener('focus', function () {
    stopPlaceholder(); // za fokusem je rotující text rušivý
    if (input.value.trim().length < MIN_CHARS) runZero();
  });
  // Po Esc/scroll-zavření drží input focus — další klik do něj musí
  // zero-state otevřít znovu (focus event už nepřijde).
  input.addEventListener('click', function () {
    if (own && !own.children.length && input.value.trim().length < MIN_CHARS) runZero();
  });
  input.addEventListener('blur', function () {
    if (!input.value) startPlaceholder();
  });

  // klik mimo hlavičku zavře náš panel a vrátí rotaci placeholderu
  document.addEventListener('pointerdown', function (e) {
    if (!host.contains(e.target)) {
      closeOwn();
      if (!input.value) startPlaceholder();
    }
  });

  // Tab ven z hledání nesmí nechat panel viset (klávesnicová past, nález U1).
  // Klik na nefokusovatelný řádek panelu vyvolá focusout s aktivním <body> —
  // časová značka pointerdownu uvnitř hosta takové zavření přeskočí, jinak by
  // panel zmizel dřív, než doběhne click.
  let hostPointerAt = 0;
  host.addEventListener('pointerdown', function () {
    hostPointerAt = Date.now();
  });
  // V otevřeném mobilním sheetu (mobileSearch.js, `body.bs-search-open`) panel
  // NEZAVÍRÁ ani ztráta fokusu, ani scroll: sheet sám schovává klávesnici blurem
  // inputu při dotyku mimo pole a scrollem výsledků (zadání Tomáše 2026-09-09),
  // a schování klávesnice na iPhonu střílí `scroll`/`resize` — s driftem
  // zavřel panel dřív, než doběhl click na produkt („první tap nic neudělá").
  // Sheet zavírá jen tlačítko zpět / Esc, a to blur pouští až PO odebrání třídy.
  host.addEventListener('focusout', function () {
    setTimeout(function () {
      if (inSheet()) return;
      if (Date.now() - hostPointerAt < 400) return;
      const focused = document.activeElement;
      if (focused && host.contains(focused)) return;
      closeOwn();
      if (!input.value) startPlaceholder();
    }, 0);
  });

  // Otevřený panel jede se sticky headerem přes celou stránku — po odscrollování
  // o kus dál už je z něj jen clona přes obsah (nález U1: zakrýval i patičku).
  // Akumulovaný drift, ne baseline: scroll event přijde až PO posunu, takže
  // jednorázový skok (kotva, PageDown) by s baseline nikdy nezavřel.
  let scrollDrift = 0;
  // ⚠ `window.scrollY` při initu vynucoval layout uprostřed bootu (337–575 ms
  // self-time při CPU 4×, profil 2026-09-11) — čteme ho až při prvním scrollu,
  // kdy je layout stejně čerstvý. `null` = ještě nemáme s čím porovnávat.
  let lastScrollY = null;
  window.addEventListener(
    'scroll',
    function () {
      const y = window.scrollY;
      const delta = lastScrollY === null ? 0 : Math.abs(y - lastScrollY);
      lastScrollY = y;
      if (inSheet()) {
        scrollDrift = 0;
        return;
      }
      if (own && own.children.length) {
        scrollDrift += delta;
        if (scrollDrift > 80) {
          scrollDrift = 0;
          closeOwn();
        }
      } else {
        scrollDrift = 0;
      }
    },
    { passive: true }
  );
}
